import { useTranslations } from 'next-intl';
import { type FC, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form/dist/types/form';

import { zodResolver } from '@hookform/resolvers/zod/dist/zod';

import { isValidPhoneNumber } from 'libphonenumber-js';
import { z } from 'zod';

import { Flex } from '@/components/common/Flex';
import { FormPhoneInput } from '@/components/common/form/FormPhoneInput';
import { FormSelect } from '@/components/common/form/FormSelect';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { FormTextInput } from '@/components/common/form/FormTextInput';
import { Grid } from '@/components/common/Grid';
import { AtIcon } from '@/components/common/icons/AtIcon';
import { AreaSelect } from '@/components/common/select/AreaSelect';
import { CurrencySelect } from '@/components/common/select/CurrencySelect';
import { MonthTurnoverSelect } from '@/components/common/select/MonthTurnoverSelect';
import { Text } from '@/components/common/Text';
import { TextLink } from '@/components/common/TextLink';
import { urls } from '@/constants/urls';

import styles from './RegistrationForm.module.scss';

const TELEGRAM_NICKNAME_REGEXP = /^@?[a-zA-Z][a-zA-Z0-9_]{4,31}$/;

const useFormSchema = () => {
  const tErrors = useTranslations('errors');

  return useMemo(
    () =>
      z
        .object({
          email: z
            .string()
            .trim()
            .min(1, tErrors('field is required'))
            .email(tErrors('incorrect format'))
            .toLowerCase(),
          password: z.string().min(1, tErrors('field is required')),
          passwordRepeat: z.string(),
          company_website: z
            .string({
              required_error: tErrors('field is required'),
            })
            .min(1, tErrors('field is required')),
          month_turnover: z
            .string({
              required_error: tErrors('field is required'),
            })
            .min(1, tErrors('field is required')),
          payments_currencies: z
            .array(
              z.object({
                id: z.string(),
              })
            )
            .min(1, tErrors('field is required')),
          payments_geo: z
            .string({
              required_error: tErrors('field is required'),
            })
            .min(1, tErrors('field is required')),
          phone: z
            .string({
              required_error: tErrors('field is required'),
            })
            .min(1, tErrors('field is required'))
            .refine((value) => isValidPhoneNumber(value), tErrors('incorrect format')),
          telegram: z
            .string()
            .trim()
            .refine((value) => value.length === 0 || TELEGRAM_NICKNAME_REGEXP.test(value), {
              message: tErrors('incorrect format'),
            }),
        })
        .refine((data) => data.password === data.passwordRepeat, {
          message: tErrors('passwords must match'),
          path: ['passwordRepeat'],
        }),
    [tErrors]
  );
};

export type RegistrationFormData = z.infer<ReturnType<typeof useFormSchema>>;

type RegistrationFormProps = {
  onSubmit: SubmitHandler<RegistrationFormData>;
};

export const RegistrationForm: FC<RegistrationFormProps> = (props) => {
  const t = useTranslations('pages.registration.form');

  const { onSubmit } = props;

  const form = useForm<RegistrationFormData>({
    resolver: zodResolver(useFormSchema()),
    mode: 'onBlur',
    defaultValues: {
      payments_currencies: [],
      phone: '',
      telegram: '',
    },
  });

  const { register, handleSubmit } = form;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap={9}>
          <Flex className={styles.titleBlock} direction="column" gap={4}>
            <Text type="medium64" whiteSpace="preWrap">
              {t('title')}
            </Text>

            <Text type="regular18" color="white-50p">
              {t('subtitle')}
            </Text>
          </Flex>

          <Flex className={styles.formContainer} direction="column">
            <Grid className={styles.fields} gap={4}>
              <Flex direction="column" gap={4}>
                <FormTextInput
                  {...register('email')}
                  type="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="email"
                  required
                  placeholder={t('fields.email.placeholder')}
                />

                <FormTextInput
                  {...register('password')}
                  type="password"
                  placeholder={t('fields.password.placeholder')}
                  autoComplete="new-password"
                />

                <FormTextInput
                  {...register('passwordRepeat')}
                  type="password"
                  placeholder={t('fields.passwordRepeat.placeholder')}
                  autoComplete="new-password"
                />

                <FormTextInput
                  {...register('company_website')}
                  required
                  placeholder={t('fields.company_website.placeholder')}
                />
              </Flex>

              <Flex direction="column" gap={4}>
                <FormSelect
                  {...register('month_turnover')}
                  component={MonthTurnoverSelect}
                  required
                  placeholder={t('fields.month_turnover.placeholder')}
                />

                <FormSelect
                  {...register('payments_currencies')}
                  component={CurrencySelect}
                  placeholder={t('fields.payments_currencies.placeholder')}
                  noAuth
                  availableCurrency={false}
                />

                <FormSelect
                  {...register('payments_geo')}
                  component={AreaSelect}
                  required
                  placeholder={t('fields.payments_geo.placeholder')}
                />

                <FormPhoneInput name="phone" required placeholder={t('fields.phone.placeholder')} />

                <FormTextInput
                  {...register('telegram')}
                  placeholder={t('fields.telegram.placeholder')}
                  prefix={<AtIcon />}
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="off"
                />
              </Flex>
            </Grid>
            <Flex direction="column" gap={4}>
              <FormSubmitButton>{t('submitButton')}</FormSubmitButton>

              <Flex direction="column" gap={2}>
                <Text className={styles.alreadyHaveAccount} type="regular14" color="white-50p">
                  {t.rich('alreadyHaveAccount', {
                    'login-link': (children) => <TextLink href={urls.Login}>{children}</TextLink>,
                  })}
                </Text>

                <Text type="regular14" color="dark-green2-32p">
                  {t('bottomNote')}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
